import React, { useContext, useEffect } from 'react';
import { useI18next } from 'gatsby-plugin-react-i18next';
import { Helmet } from 'react-helmet';

import Layout from '@components/layout';
import SEO from '@components/SEO';
import TeamInfoModal from '@components/TeamInfoModal';
import TeamSelectionModal from '@components/TeamSelectionModal';

import Head from '@components/Home/Head';
import Features from '@components/Home/Features';
import IVA from '@components/Home/IVA';
import Reviews from '@components/Sections/evangelist/Reviews';
import EditorSection from '@components/Sections/EditorSection';
import TipsHome from '@components/Home/Tips';
import PricingSection from '@components/Sections/PricingSection';
import WorkflowHome from '@components/Home/WorkflowHome';
import TemplateGallery from '@components/Sections/TemplateGallery';

import useWindowSize from '@hooks/useWindowSize';

import { DispatchContext, StateContext } from '@store/store';
import {
  openAuthModal,
  openTeamInviteInfoModal,
  openTeamSelectionModal,
} from '@store/actions';
import API from '../lib/API';
import StickyFilmr from '@components/Filmr/StickyFilmr';
import useDeviceDetect from '@hooks/useDeviceDetect';
import { getCookieValue, setCookie } from '@utils/cookie';
import { getEditorHost, updateEditorAuthCookie } from '@utils/editor';

const AuthAPI = new API();

function Home(props) {
  const { t } = useI18next();
  const { isMediaMatched } = useWindowSize('max-width: 640px');
  const dispatch = useContext(DispatchContext);
  const { deviceType } = useDeviceDetect();
  const globalStore = useContext(StateContext);

  useEffect(() => {
    if (
      globalStore.teamSelectionModal &&
      globalStore.teamSelectionModal.state
    ) {
      return;
    }
    if (localStorage.getItem(window.btoa('user'))) {
      window.location.href = '/workflow/marketing-templates';
    } else {
      checkForTeamInvite();
    }
  }, []);

  useEffect(() => {
    if (deviceType && deviceType !== 'desktop') {
      const ivwlpCookie = getCookieValue('ivwlp');
      if (!ivwlpCookie) {
        const nextYear = new Date(new Date().getTime() + 86400 * 365 * 1000);
        setCookie({
          name: 'ivwlp',
          value: 'old',
          path: '/',
          expires: nextYear,
        });
      }
    }
  }, [deviceType]);

  function checkForTeamInvite() {
    if (
      globalStore.teamInviteInfoModal &&
      globalStore.teamInviteInfoModal.state
    ) {
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const action = queryParams.get('action') || '';
    if (decodeURIComponent(action) !== btoa('team-invite')) {
      return;
    }
    dispatch(openTeamInviteInfoModal({ error: false, code: null }));
  }

  function workflow(url, btnP = '-') {
    const link = typeof url === 'string' ? url : '/';
    if (localStorage.getItem(window.btoa('user'))) {
      updateEditorAuthCookie();
      let urlEditor = getEditorHost();
      if (
        deviceType !== 'desktop'
        //   || ![
        //   '/make/free-intro-maker-without-watermark/',
        //   '/make/online-video-editor/',
        // ].includes(window.location.pathname)
      ) {
        urlEditor = `${urlEditor}/workflow/marketing-templates`;
      } else {
        if (url?.includes('workflow/marketing-templates/categories')) {
          urlEditor = `${urlEditor}/${url}`;
        } else {
          const editorParams = globalStore.editorParams;
          urlEditor = `${urlEditor}/editor?id=10456&dimension=16:9&workflow=story&searchTerm=${
            editorParams?.searchTerm ? editorParams.searchTerm : ''
          }&cacheRefresh=true${
            editorParams?.tabType ? '&tabType=uploads' : ''
          }${editorParams?.categoryTray ? '&category=intro' : ''}`;
        }
      }
      window.open(urlEditor, '_self');
    } else {
      // eslint-disable-next-line no-lonely-if
      if (link === '') {
        sessionStorage.removeItem('redirectTo');
      } else {
        sessionStorage.setItem('redirectTo', link);
      }
      AuthAPI.sendAmplitudeEvent('Click Signup', { btnPosition: btnP });
      dispatch(openAuthModal({ tab: 'signup' }));
    }
  }

  return (
    <Layout location={props.location}>
      <Helmet>
        <meta
          name="google-site-verification"
          content="ywOOKd96bDgGs-0wzaQ2K7qrsuucrTty9iOwJUL7bn4"
        />
        <link rel="profile" href="https://gmpg.org/xfn/11" />
      </Helmet>
      <SEO
        seo={{
          Title: 'Online Video Editor | Video Creator | InVideo',
          Description:
            "InVideo's Online Video Editor Helps You Make Professional Videos From Premium Templates, Images, And Music.",
          CanonicalURL: 'https://invideo.io/',
        }}
      />
      <Head workflow={workflow} />

      {!isMediaMatched && (
        <EditorSection t={t} workflow={workflow} btnPosition="center" />
      )}

      <Features workflow={workflow} />
      <IVA />

      <Reviews />

      <section
        className="section our-customers py-4 md:hidden"
        id="our-cus"
        style={{
          backgroundColor: '#ebf2fa',
        }}
      >
        <div className="brand-wrapper text-center">
          <h4 className="partners-title text-xl text-brandPrimary p-4">
            Our customers:
          </h4>
        </div>
        <div className="home-brands-v2 desktop-v2 mobile-v2 my-4 flex flex-wrap justify-center items-center">
          <div className="brand-wrapper w-2/5 m-2 p-2 ">
            <picture>
              <source
                type="image/webp"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/at_t.webp"
              />
              <source
                type="image/png"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/at_t_tiny.png"
              />
              <img
                alt="Our Customer AT"
                loading="lazy"
                src="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/at_t_tiny.png"
              />
            </picture>
          </div>
          <div className="brand-wrapper w-2/5 m-2 p-2 ">
            <picture>
              <source
                type="image/webp"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/microsoft.webp"
              />
              <source
                type="image/png"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/microsoft_tiny.png"
              />
              <img
                alt="Our Customer Microsoft"
                loading="lazy"
                src="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/microsoft_tiny.png"
              />
            </picture>
          </div>
          <div className="brand-wrapper w-2/5 m-2 p-2 ">
            <picture>
              <source
                type="image/webp"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/aspen.webp"
              />
              <source
                type="image/png"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/aspen_tiny.png"
              />
              <img
                alt="Our Customer ASPEN"
                loading="lazy"
                src="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/aspen_tiny.png"
              />
            </picture>
          </div>
          <div className="brand-wrapper w-2/5 m-2 p-2 ">
            <picture>
              <source
                type="image/webp"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/reuters.webp"
              />
              <source
                type="image/png"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/reuters.png"
              />
              <img
                alt="Our Customer Reuters"
                loading="lazy"
                src="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/reuters.png"
              />
            </picture>
          </div>
          <div className="brand-wrapper w-2/5 m-2 p-2 ">
            <picture>
              <source
                type="image/webp"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/zoho.webp"
              />
              <source
                type="image/png"
                srcSet="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/zoho.png"
              />
              <img
                alt="Our Customer ZOHO"
                loading="lazy"
                src="https://d360zdw5tkn3j9.cloudfront.net/Template_Block_Assets/landing-page/zoho.png"
              />
            </picture>
          </div>
        </div>
      </section>

      <TemplateGallery
        t={t}
        data={{
          SEOKey: 'invideo-home-page',
          Title: '5000+ premium video templates',
          SubTitle: 'Jumpstart your video with professional-grade designs',
        }}
        titleClass="text-brandPrimary"
        workflow={workflow}
        btnPosition="center"
        bgGrey
      />

      <TipsHome workflow={workflow} />

      <WorkflowHome />

      <PricingSection t={t} />

      {globalStore.teamInviteInfoModal &&
        globalStore.teamInviteInfoModal.state && (
          <TeamInfoModal state={globalStore.teamInviteInfoModal} />
        )}

      {globalStore.teamSelectionModal &&
        globalStore.teamSelectionModal.state && (
          <TeamSelectionModal state={globalStore.teamSelectionModal.data} />
        )}

      {/* <StickyFilmr
        showFilmrPopup
        filmrLinks={{
          iOS: 'https://filmrapp.onelink.me/9iEj/ios',
          android: 'https://filmrapp.onelink.me/AezS/android',
        }}
      /> */}
    </Layout>
  );
}

export default Home;
